import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import { supabase } from '../../lib/supabaseClient';

import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';

const ClientSignup = () => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    type: 'practice',
    organizationName: '',
    name: '',
    phone: '',
    memo: ''
  });

  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [doneMessage, setDoneMessage] = useState('');

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex?.test(email);
  };

  const validatePhone = (phone) => {
    return /^01[0-9]-?[0-9]{3,4}-?[0-9]{4}$/?.test(String(phone || '').replace(/\s/g, ''));
  };

  const handleChange = (key, value) => {
    setFormData(prev => ({
      ...prev,
      [key]: value
    }));

    if (errors?.[key]) {
      setErrors(prev => ({
        ...prev,
        [key]: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    const nextErrors = {};

    const emailTrimmed = formData?.email?.trim();
    const passwordTrimmed = formData?.password?.trim();

    if (!emailTrimmed) nextErrors.email = '이메일을 입력해주세요';
    else if (!validateEmail(emailTrimmed)) nextErrors.email = '올바른 이메일 형식이 아닙니다';

    if (!passwordTrimmed) nextErrors.password = '비밀번호를 입력해주세요';
    else if (passwordTrimmed.length < 6) nextErrors.password = '비밀번호는 최소 6자 이상이어야 합니다';

    if (!formData?.organizationName?.trim()) nextErrors.organizationName = '시설/단체명을 입력해주세요';
    if (!formData?.name?.trim()) nextErrors.name = '담당자 이름을 입력해주세요';

    if (!formData?.phone?.trim()) nextErrors.phone = '전화번호를 입력해주세요';
    else if (!validatePhone(formData?.phone)) nextErrors.phone = '올바른 전화번호 형식이 아닙니다';

    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      return;
    }

    setIsLoading(true);
    setErrors({});
    setDoneMessage('');

    try {
      const { data, error } = await supabase.auth.signUp({
        email: emailTrimmed,
        password: passwordTrimmed
      });

      if (error) {
        setErrors({ general: error?.message || '회원가입에 실패했습니다' });
        setIsLoading(false);
        return;
      }

      const userId = data?.user?.id;
      const hasSession = Boolean(data?.session?.access_token);

      if (hasSession && userId) {
        const { error: upsertError } = await supabase
          .from('profiles')
          .upsert({
            id: userId,
            role: 'clients',
            type: formData?.type,
            organization_name: formData?.organizationName,
            name: formData?.name,
            phone: formData?.phone,
            email: emailTrimmed,
            status: 'active',
            memo: formData?.memo,
            updated_at: new Date().toISOString()
          });

        if (upsertError) {
          console.group('[ClientSignup] profiles upsert error');
          console.error('message:', upsertError?.message);
          console.error('details:', upsertError);
          console.groupEnd();
          setErrors({ general: upsertError?.message || '프로필 저장에 실패했습니다' });
          setIsLoading(false);
          return;
        }

        setDoneMessage('회원가입이 완료되었습니다. 로그인 후 이용해주세요.');
        setIsLoading(false);
        return;
      }

      setDoneMessage('회원가입 요청이 완료되었습니다. 이메일 인증 후 로그인해주세요.');
      setIsLoading(false);
    } catch (err) {
      setErrors({ general: '회원가입 중 오류가 발생했습니다' });
      setIsLoading(false);
    }
  };

  const typeOptions = [
    { value: 'practice', label: '연습장 운영자' },
    { value: 'club', label: '클럽 운영자' },
    { value: 'court', label: '코트 운영자' }
  ];

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-xl bg-card border border-border rounded-2xl shadow-sm p-6">
        <div className="flex items-start justify-between gap-3 mb-6">
          <div>
            <h1 className="text-2xl font-heading font-bold text-foreground">클라이언트 회원가입</h1>
            <p className="text-sm caption text-muted-foreground mt-2">운영자 계정을 생성합니다</p>
          </div>
          <Button variant="ghost" size="sm" iconName="LogIn" iconPosition="left" onClick={() => navigate('/admin-login')}>
            로그인
          </Button>
        </div>

        {doneMessage ? (
          <div className="bg-muted border border-border rounded-xl p-4">
            <div className="flex items-start gap-3">
              <Icon name="CheckCircle2" size={18} className="text-muted-foreground mt-0.5" />
              <p className="text-sm text-foreground">{doneMessage}</p>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-5">
            {errors?.general && (
              <div className="bg-error/10 border border-error/20 rounded-xl p-4 flex items-start gap-2">
                <Icon name="AlertCircle" size={18} className="text-error mt-0.5" />
                <p className="text-sm text-error">{errors?.general}</p>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="이메일"
                name="email"
                type="email"
                value={formData?.email}
                onChange={(e) => handleChange('email', e?.target?.value)}
                error={errors?.email}
                disabled={isLoading}
                required
              />

              <Input
                label="비밀번호"
                name="password"
                type="password"
                value={formData?.password}
                onChange={(e) => handleChange('password', e?.target?.value)}
                error={errors?.password}
                disabled={isLoading}
                required
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Select
                label="유형"
                options={typeOptions}
                value={formData?.type}
                onChange={(value) => handleChange('type', value)}
                disabled={isLoading}
              />

              <Input
                label="시설/클럽/코트 이름"
                type="text"
                value={formData?.organizationName}
                onChange={(e) => handleChange('organizationName', e?.target?.value)}
                error={errors?.organizationName}
                disabled={isLoading}
                required
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="담당자 이름"
                type="text"
                value={formData?.name}
                onChange={(e) => handleChange('name', e?.target?.value)}
                error={errors?.name}
                disabled={isLoading}
                required
              />

              <Input
                label="전화번호"
                type="tel"
                value={formData?.phone}
                onChange={(e) => handleChange('phone', e?.target?.value)}
                error={errors?.phone}
                disabled={isLoading}
                required
              />
            </div>

            <Input
              label="메모"
              type="text"
              value={formData?.memo}
              onChange={(e) => handleChange('memo', e?.target?.value)}
              disabled={isLoading}
            />

            <Button type="submit" variant="warning" loading={isLoading} fullWidth>
              회원가입
            </Button>
          </form>
        )}
      </div>
    </div>
  );
};

export default ClientSignup;
